import { useEffect, useRef, useState } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { ICONS } from "../constants/icons";
import { useUser } from "../contexts/UserContext";
import useMoveTo from "../constants/navigation";
import { CiSearch } from "react-icons/ci";
import { BsPin } from "react-icons/bs";
import { off, onValue, ref, set, update } from "firebase/database";
import { database } from "../constants/firebase";
import { ROUTES } from "../App";
import { formatTime, getChatId } from "../utils/utils";
import { FiPlus } from "react-icons/fi";
import { useLocation } from "react-router";
import ChatUserList from "../components/ChatUserList";
import GroupList from "../components/GroupsList";
import MobileHeader from "../components/MobileHeader";
import { m } from "framer-motion";


const Chats = () => {
    const navigate = useMoveTo();
    const { currentUser } = useUser();
    const location = useLocation();
    const [searchQuery, setSearchQuery] = useState("");
    const [activeTab, setActiveTab] = useState("All");
    const [usersWithChats, setUsersWithChats] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    // ✅ Filter state
    const [showFilter, setShowFilter] = useState(false);
    const [selectedNiche, setSelectedNiche] = useState("");
    const availableNiches = ["Filmmaker", "Content Creator", "Drone Operator", "Gaffer", "Sound Designer", "VFX Artist", "Editor"];

    useEffect(() => {
        if (location.state?.incommingTab) {
            setActiveTab(location.state.incommingTab);
        }
    }, [location.state]);


    useEffect(() => {
        if (!currentUser?.uid) return;

        const usersRef = ref(database, "Users");
        const friendsRef = ref(database, `Users/${currentUser.uid}/friends`);
        const pinnedRef = ref(database, `Users/${currentUser.uid}/pinnedChats`);
        const requestsRef = ref(database, `Users/${currentUser.uid}/requestChats`);

        setLoading(true);

        let dynamicUnsubs = [];

        const unsubscribeUsers = onValue(usersRef, (snapshot) => {
            if (!snapshot.exists()) {
                setUsersWithChats([]);
                setAllUsers([]);
                return;
            }

            const allUsersData = snapshot.val();

            // Save all users for search
            setAllUsers(
                Object.entries(allUsersData)
                    .filter(([uid]) => uid !== currentUser.uid)
                    .map(([uid, user]) => ({
                        uid,
                        ...user
                    }))
            );

            // Get friends
            const unsubscribeFriends = onValue(friendsRef, (friendsSnap) => {

                const friends = friendsSnap.val() || {};

                const unsubscribePinned = onValue(pinnedRef, (pinnedSnap) => {

                    const pinnedChats = pinnedSnap.val() || {};

                    const unsubscribeRequests = onValue(requestsRef, (requestsSnap) => {

                        const requestChats = requestsSnap.val() || {};

                        Object.entries(allUsersData)
                            .filter(([uid]) => uid !== currentUser.uid)
                            .forEach(([uid, user]) => {

                                const isFriend = !!friends[uid];

                                const chatId = getChatId(currentUser.uid, uid);
                                const chatRef = ref(database, `privateChats/${chatId}`);

                                const unsubscribeChat = onValue(chatRef, (chatSnap) => {

                                    const hasMessages = chatSnap.exists();

                                    // ✅ IMPORTANT FIX
                                    // show if friend OR has messages
                                    if (!hasMessages && !isFriend) return;

                                    let unread = 0;
                                    let lastMessage = null;

                                    if (hasMessages) {

                                        const msgs = Object.entries(chatSnap.val())
                                            .map(([id, val]) => ({ id, ...val }))
                                            .sort((a, b) => a.timestamp - b.timestamp);

                                        msgs.forEach((msg) => {
                                            if (msg.senderId === uid && !msg.seen) unread++;
                                        });

                                        // 🔥 Find last displayable message
                                        for (let i = msgs.length - 1; i >= 0; i--) {
                                            const msg = msgs[i];

                                            // If not a call → valid
                                            if (msg.type !== 'call') {
                                                lastMessage = msg;
                                                break;
                                            }

                                            // If call but missed → valid
                                            if (msg.type === 'call' && msg.callStatus === 'missed') {
                                                lastMessage = msg;
                                                break;
                                            }

                                            // Otherwise skip (answered / outgoing / incoming)
                                        }
                                    }

                                    setUsersWithChats((prev) => {

                                        const existing = prev.find(u => u.uid === uid);

                                        let messageText = "";
                                        let messageType = 'text';
                                        let callStatus = null;

                                        if (lastMessage) {
                                            if (lastMessage.type === 'call') {
                                                messageType = 'call';
                                                callStatus = lastMessage.callStatus;
                                                const callIcon = lastMessage.callType === 'video' ? '📹' : '📞';

                                                // Determine who initiated/received the call
                                                const isCurrentUserCaller = lastMessage.senderId === currentUser.uid;

                                                switch (lastMessage.callStatus) {
                                                    case 'missed':
                                                        if (isCurrentUserCaller) {
                                                            // I called but they missed
                                                            messageText = `${callIcon} ${lastMessage.callType === 'video' ? 'Video' : 'Audio'} call (No answer)`;
                                                        } else {
                                                            // They called but I missed
                                                            messageText = `${callIcon} Missed ${lastMessage.callType === 'video' ? 'video' : 'audio'} call`;
                                                        }
                                                        break;


                                                    default:
                                                        messageText = `Say hi 👋"`;
                                                }
                                            } else {
                                                // Regular message types
                                                messageText = lastMessage.text ||
                                                    (lastMessage.imageUrl ? "📷 Photo" :
                                                        lastMessage.audioUrl ? "🎤 Voice message" :
                                                            isFriend ? "Say hi 👋" : "No messages yet");
                                            }
                                        } else {
                                            messageText = isFriend ? "Say hi 👋" : "No messages yet";
                                        }

                                        const updatedUser = {
                                            uid,
                                            ...user,
                                            role: user.role || "Free",
                                            isTeamUser: user.isTeamUser || false,
                                            pinned: !!pinnedChats[uid],
                                            isRequest: !!requestChats[uid],
                                            unread,
                                            message: messageText,
                                            displayTime: lastMessage ? formatTime(lastMessage.timestamp) : "",
                                            lastTimestamp: lastMessage?.timestamp || 0,
                                            isFriend,
                                            // Optional: Store message metadata for styling
                                            lastMessageType: messageType,
                                            lastCallStatus: callStatus
                                        };

                                        let newList = existing
                                            ? prev.map(u => u.uid === uid ? updatedUser : u)
                                            : [...prev, updatedUser];

                                        // Always include self
                                        const selfUser = {
                                            uid: currentUser.uid,
                                            name: `${currentUser.firstName || currentUser.name || "Me"} (You)`,
                                            profilePic: currentUser.profilePic || "",
                                            pinned: false,
                                            role: currentUser.role || "Free",
                                            unread: 0,
                                            message: "Message yourself",
                                            displayTime: "",
                                            lastTimestamp: 0,
                                            isSelf: true,
                                        };

                                        newList = [
                                            selfUser,
                                            ...newList.filter(u => u.uid !== currentUser.uid)
                                        ];

                                        // Sort properly
                                        newList.sort((a, b) => {

                                            if (a.isSelf) return -1;
                                            if (b.isSelf) return 1;

                                            if (a.pinned && !b.pinned) return -1;
                                            if (!a.pinned && b.pinned) return 1;

                                            if (a.isFriend && !b.isFriend) return -1;
                                            if (!a.isFriend && b.isFriend) return 1;

                                            return b.lastTimestamp - a.lastTimestamp;
                                        });

                                        return newList;

                                    });

                                });

                                dynamicUnsubs.push(unsubscribeChat);

                            });

                        setLoading(false);

                    });

                    dynamicUnsubs.push(unsubscribeRequests);

                });

                dynamicUnsubs.push(unsubscribePinned);

            });

            dynamicUnsubs.push(unsubscribeFriends);

        });

        return () => {

            unsubscribeUsers();

            dynamicUnsubs.forEach(unsub => unsub());

            dynamicUnsubs = [];

        };

    }, [currentUser]);


    useEffect(() => {
        if (!currentUser?.uid) return;

        const groupsRef = ref(database, "Groups");
        const pinnedGroupsRef = ref(database, `Users/${currentUser.uid}/pinnedGroups`);

        const unsubscribeGroups = onValue(groupsRef, (snapshot) => {
            if (!snapshot.exists()) {
                setGroups([]);
                return;
            }

            const data = snapshot.val();
            const groupEntries = Object.entries(data);

            const unsubscribers = [];

            // Get pinned groups
            const unsubscribePinnedGroups = onValue(pinnedGroupsRef, (pinnedSnap) => {
                const pinnedGroups = pinnedSnap.val() || {};

                groupEntries.forEach(([groupId, group]) => {
                    const isHost = group.hostId === currentUser.uid;
                    const isMember = group.members && group.members[currentUser.uid];

                    if (!isHost && !isMember) return;

                    const groupChatRef = ref(database, `groupChats/${groupId}`);

                    const unsubscribeChat = onValue(groupChatRef, (chatSnap) => {
                        let unread = 0;
                        let lastMessage = null;

                        if (chatSnap.exists()) {
                            const msgs = Object.entries(chatSnap.val()).map(([id, val]) => ({ id, ...val }));
                            msgs.sort((a, b) => a.timestamp - b.timestamp);

                            msgs.forEach((msg) => {
                                // ❌ Skip messages not visible to user
                                if (msg.deletedFor?.[currentUser.uid]) return;
                                if (msg.deleted && msg.movedToTrash) return;

                                // ❌ Skip own messages
                                if (msg.senderId === currentUser.uid) return;

                                // ✅ Count only valid unseen messages
                                if (!msg.seenBy?.[currentUser.uid]) {
                                    unread++;
                                }
                            });

                            // 🔥 Find last displayable message
                            for (let i = msgs.length - 1; i >= 0; i--) {
                                const msg = msgs[i];
                                // Skip if message is deleted for current user
                                const isDeletedForMe =
                                    msg.deleted === true || // Message deleted by sender
                                    (msg.deletedFor && msg.deletedFor[currentUser.uid] === true); // Message deleted specifically for me

                                if (isDeletedForMe) continue;

                                // If not a call → valid
                                if (msg.type !== 'call') {
                                    lastMessage = msg;
                                    break;
                                }

                                // If call but missed → valid
                                if (msg.type === 'call' && msg.callStatus === 'missed') {
                                    lastMessage = msg;
                                    break;
                                }

                                // Otherwise skip (answered / outgoing / incoming)
                            }
                        }

                        setGroups((prev) => {
                            const others = prev.filter((g) => g.groupId !== groupId);

                            let messageText = "";
                            let messageType = 'text';
                            let callStatus = null;

                            if (lastMessage) {
                                if (lastMessage.type === 'call') {
                                    messageType = 'call';
                                    callStatus = lastMessage.callStatus;
                                    const callIcon = lastMessage.callType === 'video' ? '📹' : '📞';

                                    // Determine who initiated/received the call
                                    const isCurrentUserCaller = lastMessage.senderId === currentUser.uid;

                                    switch (lastMessage.callStatus) {
                                        case 'missed':
                                            if (isCurrentUserCaller) {
                                                // I called but they missed
                                                messageText = `${callIcon} ${lastMessage.callType === 'video' ? 'Video' : 'Audio'} call (No answer)`;
                                            } else {
                                                // They called but I missed
                                                messageText = `${callIcon} Missed ${lastMessage.callType === 'video' ? 'video' : 'audio'} call`;
                                            }
                                            break;

                                        default:
                                            messageText = `Say hi 👋"`;
                                    }
                                } else {
                                    // Regular message types
                                    messageText = lastMessage.text ||
                                        (lastMessage.imageUrl ? "📷 Photo" :
                                            lastMessage.audioUrl ? "🎤 Voice message" :
                                                "No messages yet");
                                }
                            } else {
                                messageText = "No messages yet";
                            }

                            const updatedGroup = {
                                groupId,
                                isGroup: true,
                                name: group.name,
                                photo: group.photo || "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346",
                                pinned: !!pinnedGroups[groupId], // ✅ Add pinned status
                                unread,
                                members: group.members || {},
                                message: messageText,
                                displayTime: lastMessage ? formatTime(lastMessage.timestamp) : "",
                                lastTimestamp: lastMessage ? lastMessage.timestamp : 0,
                            };

                            const newList = [...others, updatedGroup];

                            // Sort groups: pinned first, then by timestamp
                            return newList.sort((a, b) => {
                                if (a.pinned && !b.pinned) return -1;
                                if (!a.pinned && b.pinned) return 1;
                                return b.lastTimestamp - a.lastTimestamp;
                            });
                        });
                    });

                    unsubscribers.push(() => off(groupChatRef, "value", unsubscribeChat));
                });
            });

            unsubscribers.push(() => off(pinnedGroupsRef, "value", unsubscribePinnedGroups));

            return () => {
                unsubscribers.forEach((unsub) => unsub());
            };
        });

        return () => off(groupsRef, "value", unsubscribeGroups);
    }, [currentUser, activeTab]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleNavigate = (user) => {
        if (user.groupId) {
            navigate(`${ROUTES.GROUP_CHAT}/${user.groupId}`)
        } else {
            navigate(`${ROUTES.PRIVATE_USER_CHAT}/${user.uid}`);
        }
    }

    // Filtered users list
    const filteredGroups = groups.filter(group => {
        // ✅ Search filtering
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
            (group.name && group.name.toLowerCase().includes(query))
        );
    });


    // Update your filteredUsers logic to include groups in the 'All' tab
    // 🔹 Apply search + niche filter
    const filteredUsers = (searchQuery.trim() ? allUsers : usersWithChats)
        .filter((user) => {
            // Tab filters
            if (!searchQuery.trim()) {
                if (activeTab === "Unread" && user.unread === 0) return false;
                if (activeTab === "DMs" && user.isGroup) return false;
                if (activeTab === "Groups" && !user.isGroup) return false;
                if (activeTab === "Requests" && !user.isRequest) return false;
            }

            // Search
            if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase();
                if (!((user.name && user.name.toLowerCase().includes(query)) ||
                    (user.location && user.location.toLowerCase().includes(query)) ||
                    (user.niche && user.niche.toLowerCase().includes(query)))) {
                    return false;
                }
            }

            // Niche filter
            if (selectedNiche && user.niche !== selectedNiche) return false;

            return true;
        });

    // Create a combined list for the 'All' tab
    const getCombinedList = () => {
        if (activeTab === "Groups") {
            return filteredGroups;
        }

        if (activeTab === "DMs") {
            return filteredUsers;
        }

        if (activeTab === "All") {
            // Always combine individual chats and groups for 'All' tab
            const combined = [
                ...filteredUsers,
                ...filteredGroups.map(group => ({
                    ...group,
                    uid: group.groupId, // Ensure consistent ID property
                    isGroup: true // Mark as group for identification
                }))
            ];

            // Apply the same sorting logic as in usersWithChats
            return combined.sort((a, b) => {
                // Self chat always at top
                if (a.isSelf) return -1;
                if (b.isSelf) return 1;

                // Pinned chats next (both individual and groups)
                if (a.pinned && !b.pinned) return -1;
                if (!a.pinned && b.pinned) return 1;

                // Then by most recent
                return b.lastTimestamp - a.lastTimestamp;
            });
        }

        return filteredUsers;
    };

    const displayList = getCombinedList();

    const togglePin = (uid, isGroup = false) => {
        if (!currentUser?.uid) return;

        if (isGroup) {
            // Handle group pinning
            const groupPinRef = ref(database, `Users/${currentUser.uid}/pinnedGroups/${uid}`);
            const isPinned = groups.find((g) => g.groupId === uid)?.pinned;

            // Update local state with proper sorting
            setGroups(prev => {
                const updated = prev.map(g =>
                    g.groupId === uid ? { ...g, pinned: !isPinned } : g
                );

                // Re-sort after pinning
                return updated.sort((a, b) => {
                    if (a.pinned && !b.pinned) return -1;
                    if (!a.pinned && b.pinned) return 1;
                    return b.lastTimestamp - a.lastTimestamp;
                });
            });

            // Update Firebase
            if (isPinned) {
                set(groupPinRef, null);
            } else {
                set(groupPinRef, true);
            }
        } else {
            // Existing individual chat pinning logic
            const pinRef = ref(database, `Users/${currentUser.uid}/pinnedChats/${uid}`);
            const isPinned = usersWithChats.find((u) => u.uid === uid)?.pinned;

            setUsersWithChats((prev) =>
                prev
                    .map((u) =>
                        u.uid === uid ? { ...u, pinned: !isPinned } : u
                    )
                    .sort((a, b) => {
                        if (a.pinned && !b.pinned) return -1;
                        if (!a.pinned && b.pinned) return 1;
                        return b.lastTimestamp - a.lastTimestamp;
                    })
            );

            if (isPinned) {
                set(pinRef, null);
            } else {
                set(pinRef, true);
            }
        }
    };

    // 🟢 Simple shimmer skeleton loader component
    const ShimmerLoader = () => (
        <div className="flex flex-col space-y-4 mt-8">
            {[...Array(6)].map((_, i) => (
                <div
                    key={i}
                    className="animate-pulse flex items-center justify-between p-4 border-t border-gray-200 dark:border-[#D9D9D9]/20"
                >
                    <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-gray-300 dark:bg-gray-700 rounded-lg"></div>
                        <div>
                            <div className="w-20 h-4 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
                            <div className="w-12 h-4 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
                        </div>
                    </div>
                    <div className="w-5 h-6 bg-gray-300 dark:bg-gray-700 rounded"></div>
                </div>
            ))}
        </div>
    );


    return (
        <div className="h-screen flex flex-col bg-white dark:bg-[#1A1A1A] text-black dark:text-white">
            <MobileHeader title={"Chat"} />
            <div className="flex flex-1 overflow-hidden">
                <Sidebar />
                <main className="flex-1 min-h-0 overflow-y-auto pt-5 pb-24 md:pb-20 px-6 bg-white text-black dark:bg-[#1A1A1A] dark:text-white">
                    <div className="flex items-center justify-between">
                        <div />
                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">Chat</h2>
                        <div />
                    </div>

                    {/* Search Input */}
                    <div className="flex items-center justify-between md:mt-5 mt-1 space-x-4">
                        <div className="relative w-full md:w-full">
                            <input
                                type="text"
                                placeholder="Search here..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                className="rounded-xl pl-5 pr-10 py-3 bg-[#F5F9FE] dark:bg-white placeholder-[#6b7280] outline-none w-full text-[#262626] dark:text-[#262626] border border-gray-200 dark:border-none"
                            />
                            <CiSearch
                                className="absolute right-5 top-1/2 transform -translate-y-1/2 text-gray-500 text-2xl cursor-pointer"
                                onClick={() => { }} />

                        </div>

                        {/* 🔹 Niche Dropdown */}
                        <div className="relative w-40">
                            <select
                                value={selectedNiche}
                                onChange={(e) => setSelectedNiche(e.target.value)}
                                className="w-full px-2 pr-7 py-3.5 rounded-lg bg-primary text-white outline-none cursor-pointer
               appearance-none
               bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBkPSJNNyAxMCBsNSA1IDUtNSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIyIiBmaWxsPSJ3aGl0ZSIvPjwvc3ZnPg==')]
               bg-no-repeat bg-[position:right_0.5rem_center] bg-[size:1.5em_1.5em] md:text-sm text-xs"
                            >
                                <option value="">All Niches</option>
                                {availableNiches.map((niche) => (
                                    <option key={niche} value={niche}>
                                        {niche}
                                    </option>
                                ))}
                            </select>
                        </div>


                    </div>
                    {/* Tabs */}
                    <div className="flex mt-6 mb-2">
                        {/* {['All', 'DMs', 'Groups', 'Unread'].map(tab => ( */}
                        {['All', 'DMs', 'Groups', 'Unread', 'Requests'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`flex-1 px-0 py-2 text-sm rounded-lg font-semibold transition min-w-0 ${activeTab === tab ? 'bg-[#327691] text-white' : 'bg-transparent text-black dark:text-white'}`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                    {/* User List */}
                    <div className="mt-5">
                        {loading ? (
                            <ShimmerLoader />
                        ) : activeTab === "Groups" ? (
                            filteredGroups.length === 0 ? (
                                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                                    No Groups found.
                                </div>
                            ) : (
                                <GroupList
                                    filteredGroups={filteredGroups}
                                    navigate={navigate}
                                    currentUser={currentUser}
                                />
                            )
                        ) : displayList.length === 0 ? ( // 👈 Changed from filteredUsers to displayList
                            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                                No Chats found.
                            </div>
                        ) : (
                            <ChatUserList
                                filteredUsers={displayList} // 👈 Changed from filteredUsers to displayList
                                navigate={handleNavigate}
                                togglePin={togglePin}
                                currentUser={currentUser}
                            />
                        )}
                    </div>

                    {activeTab === "Groups" && (
                        <button
                            onClick={() => navigate(ROUTES.CREATE_GROUP)} // 👈 You can change this route/action
                            className="fixed bottom-24 md:bottom-10 right-6 bg-[#327691] hover:bg-[#285d72] text-white p-4 rounded-full shadow-lg transition-colors duration-200"
                        >
                            <FiPlus size={24} />
                        </button>
                    )}

                </main>
            </div>
        </div>
    );
};

export default Chats;
